/**
 * Live-transport session config (the live-backend seam).
 *
 * The web client runs in two modes, switched purely by `VITE_API_BASE`:
 *
 *   - **unset** → backendless demo. `restClient`/`wsClient` use the in-memory
 *     `mockTransport`; nothing here is read.
 *   - **set** → live. `restClient` issues real `fetch`es against the Worker and
 *     `wsClient` opens a real `WebSocket` to the UserHub Durable Object. Every
 *     authenticated call carries `Authorization: Bearer <jwt>` + `x-device-id`
 *     (07 §1).
 *
 * This module is the single source of truth for "are we live?" and for the
 * bearer JWT + device id. In a full build these come from the auth flow; for the
 * seam they are seeded from env (`VITE_API_BASE`, `VITE_API_JWT`,
 * `VITE_DEVICE_ID`) with a generated device id fallback persisted to
 * `localStorage`, and can be overridden at runtime via `setSession`.
 */

import { ulid } from './ids';

/** Trim a trailing slash so we can template `${base}/v1/...` uniformly. */
function normalizeBase(raw: string): string {
  return raw.replace(/\/+$/, '');
}

/** Derive the WebSocket origin (`ws(s)://…`) from the HTTP API base. */
export function toWsBase(httpBase: string): string {
  const base = normalizeBase(httpBase);
  if (base.startsWith('https://')) return 'wss://' + base.slice('https://'.length);
  if (base.startsWith('http://')) return 'ws://' + base.slice('http://'.length);
  // Protocol-relative or bare host — assume secure.
  return 'wss://' + base.replace(/^\/+/, '');
}

const RAW_API_BASE = import.meta.env.VITE_API_BASE;

/** `true` when a live backend is configured; `false` ⇒ mock transport. */
export const IS_LIVE: boolean = typeof RAW_API_BASE === 'string' && RAW_API_BASE.trim() !== '';

/** Normalized HTTP API base (e.g. `https://api.sinyal.app`). Empty when not live. */
export const API_BASE: string = IS_LIVE ? normalizeBase(RAW_API_BASE!.trim()) : '';

/** WebSocket base derived from `API_BASE`. Empty when not live. */
export const WS_BASE: string = IS_LIVE ? toWsBase(API_BASE) : '';

const DEVICE_ID_STORAGE_KEY = 'sinyal.device_id';

/** Resolve a stable device id: env → localStorage → freshly minted (persisted). */
function resolveDeviceId(): string {
  const fromEnv = import.meta.env.VITE_DEVICE_ID;
  if (typeof fromEnv === 'string' && fromEnv.trim() !== '') return fromEnv.trim();
  try {
    const stored = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (stored) return stored;
    const fresh = ulid();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // localStorage unavailable (SSR / privacy mode) — ephemeral id is fine.
    return ulid();
  }
}

interface SessionState {
  /** Bearer JWT for `Authorization`. Empty until auth completes. */
  jwt: string;
  /** Stable per-install device id (ULID) for `x-device-id`. */
  deviceId: string;
}

const state: SessionState = {
  jwt:
    typeof import.meta.env.VITE_API_JWT === 'string'
      ? import.meta.env.VITE_API_JWT.trim()
      : '',
  deviceId: resolveDeviceId(),
};

/** Current bearer JWT (may be empty before login). */
export function getJwt(): string {
  return state.jwt;
}

/** Current device id (always set). */
export function getDeviceId(): string {
  return state.deviceId;
}

/** Update the live session (called by the auth flow once tokens are known). */
export function setSession(next: { jwt?: string; deviceId?: string }): void {
  if (next.jwt !== undefined) state.jwt = next.jwt;
  if (next.deviceId !== undefined) {
    state.deviceId = next.deviceId;
    try {
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, next.deviceId);
    } catch {
      /* ignore */
    }
  }
}

/** Auth headers every non-public REST call (and the WS ticket POST) carry. */
export function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'x-device-id': state.deviceId };
  if (state.jwt) h['authorization'] = `Bearer ${state.jwt}`;
  return h;
}
